import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createProject,
  inviteMember,
  removeSecret,
  renameProject,
  restoreAccount,
  setAccountPassword,
  setProjectArchived,
  setSecret,
  settingsKeys,
  suspendAccount,
  updateMembership,
  updateWorkspaceSettings,
} from './api';
import type {
  InviteMemberInput,
  SecretKey,
  SetAccountPasswordInput,
  UpdateMembershipInput,
  WorkspaceSettingsPatch,
} from './types';

/**
 * One write hook per mutation, each invalidating the one cache entry it can
 * affect. Unlike requests (where a single write can move three different
 * lists — see useRequestMutations.ts), every settings write only ever changes
 * the list it belongs to, so there is no shared "write caches" helper to
 * factor out here.
 */

export function useUpdateWorkspaceSettings(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: WorkspaceSettingsPatch) => updateWorkspaceSettings(workspaceId, patch),
    onSuccess: (fresh) => queryClient.setQueryData(settingsKeys.workspace(workspaceId), fresh),
  });
}

export function useSetSecret(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: SecretKey; value: string }) => setSecret(workspaceId, key, value),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.secrets(workspaceId) }),
  });
}

export function useRemoveSecret(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: SecretKey) => removeSecret(workspaceId, key),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.secrets(workspaceId) }),
  });
}

export function useInviteMember(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteMemberInput) => inviteMember(workspaceId, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.people(workspaceId) }),
  });
}

export function useUpdateMembership(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, patch }: { membershipId: string; patch: UpdateMembershipInput }) =>
      updateMembership(workspaceId, membershipId, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.people(workspaceId) }),
  });
}

/**
 * The three account writes.
 *
 * Each returns the fresh account, so the row is written straight into its own
 * cache entry rather than refetched — the server has just told us the answer.
 *
 * Suspension additionally invalidates the people list, and that is not
 * belt-and-braces: a suspended account is a fact about the person, so the row
 * beside the panel has to stop claiming they are simply Active. The password
 * writes touch nothing on that list, so they do not invalidate it.
 */
export function useSetAccountPassword(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, input }: { membershipId: string; input: SetAccountPasswordInput }) =>
      setAccountPassword(workspaceId, membershipId, input),
    onSuccess: (fresh) =>
      queryClient.setQueryData(settingsKeys.account(workspaceId, fresh.membershipId), fresh),
  });
}

export function useSuspendAccount(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, reason }: { membershipId: string; reason: string }) =>
      suspendAccount(workspaceId, membershipId, reason),
    onSuccess: (fresh) => {
      queryClient.setQueryData(settingsKeys.account(workspaceId, fresh.membershipId), fresh);
      void queryClient.invalidateQueries({ queryKey: settingsKeys.people(workspaceId) });
    },
  });
}

export function useRestoreAccount(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) => restoreAccount(workspaceId, membershipId),
    onSuccess: (fresh) => {
      queryClient.setQueryData(settingsKeys.account(workspaceId, fresh.membershipId), fresh);
      void queryClient.invalidateQueries({ queryKey: settingsKeys.people(workspaceId) });
    },
  });
}

export function useCreateProject(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createProject(workspaceId, name),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.projects(workspaceId) }),
  });
}

export function useRenameProject(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameProject(workspaceId, id, name),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.projects(workspaceId) }),
  });
}

export function useSetProjectArchived(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      setProjectArchived(workspaceId, id, archived),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.projects(workspaceId) }),
  });
}
